import { Row, Col } from 'antd';
import config from 'config';
import { format } from 'date-fns';
import { ProductOutput } from 'gql/types';
import { NavbbLogo } from 'modules/common/components';
import ProductQR from 'modules/products/components/ProductQR';
import { CATEGORY_ID } from 'modules/products/constants/categoryId';
import { LabelSize } from 'modules/products/enums';
import { useWoocommerceCategories } from 'modules/products/hooks/useWoocommerceCategories';
import { getCryoType } from 'modules/products/utils/getCryoType';
import { getRecommendationsByCategoryId } from 'modules/products/utils/getRecommendationsByCategory';
import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QR_SIZES,
  LABEL_SIZE_STYLES,
  BLOOD_TYPE_TRANSLATIONS,
  LABEL_GUTTER,
  DONOR_SPECIES,
} from './config';
import styles from './styles.module.scss';

type PropTypes = {
  product: ProductOutput;
  size?: LabelSize;
};

const Label = forwardRef<any, PropTypes>(
  ({ product, size = LabelSize.Small }, ref) => {
    const { donation, woocommerceCategoryId, expirationDate } = product;
    const { t } = useTranslation('products.Label');

    const { categories } = useWoocommerceCategories();

    const containerClassNames = `${styles.container} ${LABEL_SIZE_STYLES[size]}`;
    const bloodType =
      donation.donor.bloodType &&
      BLOOD_TYPE_TRANSLATIONS[donation.donor.bloodType];
    const productBloodIdentifier = product.bloodIdentifiers?.[0];
    const gender = DONOR_SPECIES[donation.donor.species];
    const qrSize = QR_SIZES[size];

    const productCategory = categories?.find(
      (category) => category.id === woocommerceCategoryId
    );

    const productCategoryLabel = useMemo(() => {
      if (!productCategory) {
        return '';
      }

      if (productCategory.id === CATEGORY_ID.UNCATEGORIZED) {
        return t('wholeBlood');
      }

      return productCategory.name;
    }, [productCategory]);

    const storageRecommendations = getRecommendationsByCategoryId(
      woocommerceCategoryId
    );

    const cryoType = getCryoType(product.woocommerceProductId);

    const productTypeLabel = cryoType ?? productCategoryLabel;

    return (
      <div ref={ref} className={containerClassNames}>
        {product.isMantelAssigned && (
          <span className={styles.mantelBadge}>M</span>
        )}
        <Row gutter={LABEL_GUTTER[size]}>
          <Col span={24}>
            <div className={styles.content}>
              <Row>
                <Col>
                  <NavbbLogo
                    className={styles.logo}
                    preview={false}
                    width={484}
                    height={232}
                  />
                </Col>
                {!cryoType && (
                  <Col flex={1}>
                    <Row
                      className={styles.bloodTypeContainer}
                      justify="center"
                      align="bottom"
                    >
                      <Col>
                        {t('dea1')} <br />
                        <span className={styles.bold}>{bloodType}</span>
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
          <Col span={24}>
            <div className={styles.content}>
              <Row gutter={[24, 0]}>
                <Col>
                  {t('expiresOn')}:&nbsp;
                  <span className={styles.bold}>
                    {format(new Date(expirationDate), config.DATE_FORMATS.DATE)}
                  </span>
                </Col>
                <Col>
                  {t('volume')}:&nbsp;
                  <span className={styles.bold}>
                    {product.value} {t('ml')}
                  </span>
                </Col>
              </Row>
            </div>
          </Col>
          <Col span={24}>
            <div className={styles.content}>
              <Row justify="space-between" align="middle">
                <Col>
                  {t('product')}:&nbsp;
                  <span className={styles.productType}>{productTypeLabel}</span>
                </Col>
              </Row>
            </div>
          </Col>
          <Col span={24}>
            <div className={styles.content}>
              <Row align="middle">
                <Col span={24}>
                  <span className={styles.recommendationsTitle}>
                    {t('storageRecommendations')}:
                  </span>
                </Col>
                <Col span={20}>
                  <ul className={styles.recommendationsList}>
                    {storageRecommendations.map((recommendation, i) => {
                      return (
                        <li key={`${recommendation}_${i}`}>{recommendation}</li>
                      );
                    })}
                  </ul>
                </Col>
                <Col span={4}>
                  <ProductQR
                    width={qrSize}
                    height={qrSize}
                    productId={product.shortId}
                  />
                </Col>
              </Row>
            </div>
          </Col>
          <Col span={24}>
            <div className={styles.content}>
              <Row justify="space-between" gutter={4}>
                <Col span={8}>
                  {t('donationId')}: {donation.shortId}
                </Col>
                <Col span={7}>
                  {t('unitId')}: {productBloodIdentifier?.unitId}
                </Col>
                <Col span={9}>
                  {t('anticoagulant')}: {product.anticoagulant}
                </Col>
                <Col span={8}>
                  {t('species')}:&nbsp;
                  <span className={styles.species}>{gender}</span>
                </Col>
                <Col span={16}>
                  {t('dd')}:&nbsp;
                  {format(
                    new Date(donation.donationDate),
                    config.DATE_FORMATS.SHORT_DATE
                  )}
                </Col>
              </Row>
            </div>
          </Col>
          <Col span={24}>
            <Row justify="end">
              <Col className={styles.usage}>{t('forVeterinaryOnly')}</Col>
              <Col className={styles.footer} span={24}>
                {t('pleaseContact', { contact: '(703) 574 7417' })}
              </Col>
            </Row>
          </Col>
          <Col className={styles.donorName} span={24}>
            {donation.donor.name} {donation.donor.user?.lastName}
          </Col>
        </Row>
      </div>
    );
  }
);

export default Label;
